import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import EmojiPicker, { Theme, type EmojiClickData } from 'emoji-picker-react';
import * as emoji from 'node-emoji';
import {
  FiPlus,
  FiSend,
  FiPaperclip,
  FiX,
  FiArrowLeft,
  FiSmile,
  FiChevronDown,
  FiChevronUp,
  FiMaximize2,
  FiChevronLeft,
  FiChevronRight,
  FiBriefcase,
  FiSearch,
  FiFolder,
  FiExternalLink,
  FiFileText,
  FiDownload,
  FiCrop,
  FiVideo,
  FiMusic,
} from 'react-icons/fi';
import { type Area } from 'react-easy-crop';
import { FreeformImageCropModal } from '../common/FreeformImageCropModal';
import { Tooltip } from '../ui/Tooltip';
import { createCroppedImageFile } from '../../utils/imageCrop';
import {
  useGetConversationMessages,
  useMarkConversationRead,
  type ConversationItem,
  type ChatAttachment,
} from '../../api/conversations';
import { useConversationWs } from '../../hooks/useConversationWs';
import { ChatMessageBubble, FullscreenMediaViewer } from './ChatMessageBubble';
import { offerUuidFromMessage } from './offerCard';
import { useAuth } from '../../hooks/useAuth';
import { ChatAvatar, pickDisplayName } from './ChatAvatar';
import { triggerFileDownload } from '../../utils/download';
import { useGetUserProfile } from '../../api/profile';
import { toast } from '../../utils/toast';
import { useMessageQueue } from '../../hooks/useMessageQueue';
import {
  UPLOAD_MAX_FILE_SIZE_BYTES,
  formatFileSize,
  validateChatFileType,
  CHAT_FILE_ACCEPT_ATTRIBUTE,
} from '../../utils/uploadValidation';

const markedReadSet = new Set<string>();

interface ChatActivePaneProps {
  conversation: ConversationItem | null;
  highlightOfferUuid?: string;
  onBackMobile?: () => void;
  isSidebarCollapsed?: boolean;
  onToggleSidebar?: () => void;
}

const formatDateLabel = (isoString?: string) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const isYesterday = new Date(now.getTime() - 86400000).toDateString() === date.toDateString();

  if (isToday) return 'Today';
  if (isYesterday) return 'Yesterday';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
};

const getDocumentThumbnailUrl = (url: string): string | null => {
  if (!url || url.startsWith('blob:') || url.startsWith('data:')) return null;
  if (url.includes('/raw/upload/')) return null;

  if (url.includes('res.cloudinary.com')) {
    const parts = url.split('/upload/');
    if (parts.length === 2) {
      let path = parts[1];
      const match = path.match(/\.(pdf|doc|docx|ppt|pptx|xls|xlsx)$/i);
      if (match) {
        path = path.slice(0, match.index) + '.jpg';
        return `${parts[0]}/upload/c_fill,w_100,h_100,g_north,pg_1,f_jpg/${path}`; // Use 100x100 for small thumbnail
      }
    }
  }
  return null;
};

const getAttachmentDisplayInfo = (att: ChatAttachment) => {
  const url = att.url || att.file_url || '';

  const basenameFromUrl = (u: string): string => {
    if (!u) return '';
    try {
      const path = u.split('?')[0].split('#')[0];
      return decodeURIComponent(path.split('/').pop() || '');
    } catch {
      return '';
    }
  };

  const candidates = [
    att.original_filename,
    att.name,
    basenameFromUrl(att.public_id || ''),
    basenameFromUrl(url),
  ];

  const KNOWN_EXT_RE =
    /\.(pdf|docx?|xlsx?|pptx?|txt|csv|zip|rar|png|jpe?g|gif|webp|svg|mp4|mov|avi|webm|mkv|mp3|wav|ogg|m4a|flac|aac)$/i;

  let ext = '';
  let fileName = '';

  for (const candidate of candidates) {
    if (candidate && KNOWN_EXT_RE.test(candidate)) {
      fileName = candidate;
      ext = candidate.split('.').pop()!.toLowerCase();
      break;
    }
  }

  if (!fileName) {
    const mimeMap: Record<string, string> = {
      'application/pdf': 'pdf',
      'application/msword': 'doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
      'application/vnd.ms-excel': 'xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
      'application/zip': 'zip',
      'application/x-rar-compressed': 'rar',
      'text/plain': 'txt',
      'text/csv': 'csv',
      'image/jpeg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'image/webp': 'webp',
      'image/svg+xml': 'svg',
      'video/mp4': 'mp4',
      'video/quicktime': 'mov',
      'video/x-msvideo': 'avi',
      'video/webm': 'webm',
      'audio/mpeg': 'mp3',
      'audio/wav': 'wav',
      'audio/ogg': 'ogg',
      'audio/mp4': 'm4a',
    };

    const mimeExt = att.mime_type ? mimeMap[att.mime_type.toLowerCase()] : undefined;
    const bareName = candidates.find((c) => !!c && c.trim().length > 0);

    if (mimeExt) {
      fileName = bareName ? `${bareName}.${mimeExt}` : `Document.${mimeExt}`;
      ext = mimeExt;
    } else if (bareName) {
      ext = bareName.includes('.') ? bareName.split('.').pop()!.toLowerCase() : '';
      fileName = bareName;
    } else {
      fileName = 'Shared File';
      if (att.resource_type === 'image') ext = 'jpg';
      else if (att.resource_type === 'video') ext = 'mp4';
      else ext = '';
    }
  }

  // Get friendly type label
  let typeLabel = ext ? ext.toUpperCase() : 'FILE';
  if (typeLabel === 'DOC' || typeLabel === 'DOCX') typeLabel = 'Word';
  if (typeLabel === 'XLS' || typeLabel === 'XLSX') typeLabel = 'Excel';

  return { fileName, ext, typeLabel };
};

export const ChatActivePane: React.FC<ChatActivePaneProps> = ({
  conversation,
  highlightOfferUuid,
  onBackMobile,
  isSidebarCollapsed = false,
  onToggleSidebar,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentUserId = user?.id ? Number(user.id) : Number(localStorage.getItem('user_id') || 0);

  const [inputMessage, setInputMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showSelectedFileFullscreen, setShowSelectedFileFullscreen] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [isApplyingCrop, setIsApplyingCrop] = useState(false);

  // In-chat Search & Shared Media Panel state
  const [searchQuery, setSearchQuery] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [showMediaPanel, setShowMediaPanel] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<ChatAttachment | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);

  interface EmojiSuggestion {
    emoji: string;
    name: string;
  }

  const [suggestions, setSuggestions] = useState<EmojiSuggestion[]>([]);
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [searchMatchRange, setSearchMatchRange] = useState<{ start: number; end: number } | null>(
    null,
  );

  const checkEmojiSuggestions = (val: string, cursorPosition: number) => {
    const textBeforeCursor = val.substring(0, cursorPosition);
    const match = textBeforeCursor.match(/:([a-zA-Z0-9_+-]{1,20})$/);
    if (match) {
      const query = match[1];
      const colonIndex = textBeforeCursor.lastIndexOf(':' + query);
      const results = emoji.search(query);
      if (results && results.length > 0) {
        const sorted = [...results]
          .filter((item) => item.name && item.emoji)
          .sort((a, b) => {
            const aStartsWith = a.name.toLowerCase().startsWith(query.toLowerCase());
            const bStartsWith = b.name.toLowerCase().startsWith(query.toLowerCase());
            if (aStartsWith && !bStartsWith) return -1;
            if (!aStartsWith && bStartsWith) return 1;
            return a.name.localeCompare(b.name);
          })
          .slice(0, 6);

        setSuggestions(sorted);
        setSuggestionIndex(0);
        setSearchMatchRange({ start: colonIndex, end: cursorPosition });
        return;
      }
    }
    setSuggestions([]);
    setSearchMatchRange(null);
  };

  const applyEmojiSuggestion = (suggestion: EmojiSuggestion) => {
    if (!searchMatchRange || !textareaRef.current) return;
    const { start, end } = searchMatchRange;
    const emojiChar = suggestion.emoji;
    const newText =
      inputMessage.substring(0, start) + emojiChar + ' ' + inputMessage.substring(end);
    setInputMessage(newText);
    setSuggestions([]);
    setSearchMatchRange(null);

    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const newCursorPos = start + emojiChar.length + 1;
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
      }
    }, 0);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const emoji = emojiData.emoji;
    if (textareaRef.current) {
      const start = textareaRef.current.selectionStart || 0;
      const end = textareaRef.current.selectionEnd || 0;
      const newText = inputMessage.substring(0, start) + emoji + inputMessage.substring(end);
      setInputMessage(newText);

      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          const newCursor = start + emoji.length;
          textareaRef.current.setSelectionRange(newCursor, newCursor);
          textareaRef.current.style.height = 'auto';
          textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
        }
      }, 0);
    } else {
      setInputMessage((prev) => prev + emoji);
    }
  };

  const conversationUuid = conversation?.conversation_uuid;

  useConversationWs(conversationUuid || undefined);

  const {
    data: infiniteData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetConversationMessages(conversationUuid || '', {
    refetchInterval: false,
  });

  const messages = useMemo(() => {
    if (!infiniteData?.pages) return [];
    return infiniteData.pages.flatMap((page: any) => {
      if (Array.isArray(page)) return page;
      if (Array.isArray(page?.messages)) return page.messages;
      if (Array.isArray(page?.data)) return page.data;
      if (Array.isArray(page?.results)) return page.results;
      if (Array.isArray(page?.data?.results)) return page.data.results;
      return [];
    });
  }, [infiniteData]);

  const markReadMutation = useMarkConversationRead();
  const { queuedMessages, enqueue, retryMessage } = useMessageQueue(conversationUuid);

  // Combine server messages + queued messages and sort by creation timestamp
  const sortedMessages = useMemo(() => {
    const fetched = [...messages];

    const queuedAsMessages = queuedMessages.map((qm) => ({
      id: qm.localId,
      conversation_id: qm.conversationUuid,
      sender_id: currentUserId,
      body: qm.body || (qm.file ? `Shared a file: ${qm.file.name}` : ''),
      created_at: new Date(qm.createdAt).toISOString(),
      queueStatus: qm.status,
      uploadProgress: qm.uploadProgress,
      localId: qm.localId,
      attachments: qm.attachments
        ? qm.attachments.map((a) => ({
            public_id: a.public_id,
            url: a.url,
            mime_type: a.mime_type,
            file_size: a.file_size,
            name: a.name,
            original_filename: a.original_filename || a.name,
            resource_type: a.resource_type,
          }))
        : qm.file
          ? [
              {
                name: qm.file.name,
                original_filename: qm.file.name,
                file_size: qm.file.size,
                mime_type: qm.fileMimeType || 'application/octet-stream',
                resource_type: qm.fileResourceType || 'raw',
                url: filePreviewUrl || '',
              },
            ]
          : undefined,
    }));

    const filteredQueue = queuedAsMessages.filter(
      (qm) => !fetched.some((m) => m.id === qm.id || (m as any).localId === qm.localId),
    );

    const combined = [...fetched, ...filteredQueue];
    return combined.sort(
      (a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime(),
    );
  }, [messages, queuedMessages, currentUserId, filePreviewUrl]);

  // Shared attachments panel data
  const sharedAttachments = useMemo(() => {
    const items: ChatAttachment[] = [];
    sortedMessages.forEach((m) => {
      if (m.attachments && m.attachments.length > 0) {
        items.push(...m.attachments);
      }
    });
    return items;
  }, [sortedMessages]);

  // In-chat search filtering
  const matchingMessages = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return sortedMessages.filter(
      (m) =>
        m.body?.toLowerCase().includes(q) ||
        m.attachments?.some((att: any) => att.name?.toLowerCase().includes(q)),
    );
  }, [sortedMessages, searchQuery]);

  const otherParticipant =
    conversation?.other_participant ||
    conversation?.participants?.find((p) => p.id !== currentUserId) ||
    conversation?.participants?.[0];

  const otherUserId = otherParticipant?.id;

  const { data: otherProfile } = useGetUserProfile(otherUserId, Boolean(otherUserId));

  const markReadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debouncedMarkRead = useCallback((uuid: string, mutate: (uuid: string) => void) => {
    if (markedReadSet.has(uuid)) return;
    if (markReadTimerRef.current) clearTimeout(markReadTimerRef.current);
    markReadTimerRef.current = setTimeout(() => {
      if (markedReadSet.has(uuid)) return;
      markedReadSet.add(uuid);
      mutate(uuid);
      setTimeout(() => markedReadSet.delete(uuid), 30000);
    }, 500);
  }, []);

  useEffect(() => {
    const unread = conversation?.unread_count ?? 0;
    if (conversationUuid && unread > 0) {
      debouncedMarkRead(conversationUuid, markReadMutation.mutate);
    }
  }, [conversationUuid, conversation?.unread_count, debouncedMarkRead, markReadMutation.mutate]);

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  const prevScrollHeightRef = useRef<number | null>(null);
  const lastMessageIdRef = useRef<string | null>(null);
  const userScrolledUpRef = useRef<boolean>(false);
  const paginationGuardRef = useRef<boolean>(false);
  const wasFetchingRef = useRef<boolean>(false);
  const hasInitiallyScrolledRef = useRef<boolean>(false);
  const lastScrollTopRef = useRef<number>(0);

  // Restore scroll position after older messages page load
  useEffect(() => {
    if (wasFetchingRef.current && !isFetchingNextPage) {
      if (scrollContainerRef.current && prevScrollHeightRef.current !== null) {
        const container = scrollContainerRef.current;
        const diff = container.scrollHeight - prevScrollHeightRef.current;
        if (diff > 0) {
          container.scrollTop += diff;
        }
        const savedPrevScrollHeight = prevScrollHeightRef.current;
        const initialDiff = diff > 0 ? diff : 0;
        requestAnimationFrame(() => {
          if (container && savedPrevScrollHeight !== null) {
            const finalDiff = container.scrollHeight - savedPrevScrollHeight;
            const adjust = finalDiff - initialDiff;
            if (adjust !== 0) {
              container.scrollTop += adjust;
            }
          }
        });
        prevScrollHeightRef.current = null;
      }
      setTimeout(() => {
        paginationGuardRef.current = false;
      }, 400);
    }
    wasFetchingRef.current = isFetchingNextPage;
    if (!isFetchingNextPage && !wasFetchingRef.current) {
      paginationGuardRef.current = false;
    }
  }, [isFetchingNextPage]);

  // Reset scroll state on conversation switch
  useEffect(() => {
    lastMessageIdRef.current = null;
    prevScrollHeightRef.current = null;
    userScrolledUpRef.current = false;
    paginationGuardRef.current = false;
    hasInitiallyScrolledRef.current = false;
  }, [conversationUuid]);

  // Initial scroll to bottom once messages load
  useEffect(() => {
    if (!isLoading && sortedMessages.length > 0 && !hasInitiallyScrolledRef.current) {
      hasInitiallyScrolledRef.current = true;
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
        }
      });
    }
  }, [isLoading, sortedMessages.length]);

  const offerScrollRef = useRef<string | null>(null);
  useEffect(() => {
    if (!highlightOfferUuid || isLoading || sortedMessages.length === 0) return;
    if (offerScrollRef.current === highlightOfferUuid) return;
    const match = sortedMessages.find(
      (message) => offerUuidFromMessage(message) === highlightOfferUuid,
    );
    if (!match?.id) return;
    offerScrollRef.current = highlightOfferUuid;
    requestAnimationFrame(() => {
      document.getElementById(`msg-${match.id}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    });
  }, [highlightOfferUuid, isLoading, sortedMessages]);

  // Track newest message for auto-scroll without snapping on read-receipts
  const newestMessageId = useMemo(() => {
    if (sortedMessages.length === 0) return null;
    return sortedMessages[sortedMessages.length - 1]?.id ?? null;
  }, [sortedMessages]);

  useEffect(() => {
    if (!newestMessageId || newestMessageId === lastMessageIdRef.current) return;
    lastMessageIdRef.current = newestMessageId;

    const newestMsg = sortedMessages.find((m) => m.id === newestMessageId);
    const isCurrentUser = newestMsg?.sender_id === currentUserId;

    if (isCurrentUser) {
      userScrolledUpRef.current = false;
      scrollToBottom('smooth');
    } else if (!userScrolledUpRef.current) {
      scrollToBottom('auto');
    }
  }, [newestMessageId, sortedMessages, currentUserId]);

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;

    // Detect scroll direction (true if scrolling UP)
    const isScrollingUp = scrollTop < lastScrollTopRef.current;
    lastScrollTopRef.current = scrollTop;

    // Trigger pagination when scrolling UP near the top (guarded against duplicate triggers)
    if (
      isScrollingUp &&
      scrollTop <= 150 &&
      hasNextPage &&
      !isFetchingNextPage &&
      !paginationGuardRef.current
    ) {
      paginationGuardRef.current = true;
      prevScrollHeightRef.current = scrollHeight;
      fetchNextPage();
    }
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 80;
    userScrolledUpRef.current = !isNearBottom;
    setShowScrollButton(!isNearBottom);
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    let val = e.target.value;
    const cursor = e.target.selectionStart || val.length;

    // Convert complete shortcodes like :cat: or :smile: directly
    const emojified = emoji.emojify(val);
    if (emojified !== val) {
      val = emojified;
    }

    setInputMessage(val);
    checkEmojiSuggestions(val, cursor);

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  };

  const getMimeType = (file: File): string => {
    if (file.type) return file.type;
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    const mimeMap: Record<string, string> = {
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      webp: 'image/webp',
      mp4: 'video/mp4',
      mov: 'video/quicktime',
      avi: 'video/x-msvideo',
      webm: 'video/webm',
      mp3: 'audio/mpeg',
      wav: 'audio/wav',
      ogg: 'audio/ogg',
      m4a: 'audio/mp4',
      pdf: 'application/pdf',
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      xls: 'application/vnd.ms-excel',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      zip: 'application/zip',
      rar: 'application/x-rar-compressed',
    };
    return mimeMap[ext] || 'application/octet-stream';
  };

  const getResourceType = (mimeType: string, fileName?: string): string => {
    const ext = fileName ? fileName.split('.').pop()?.toLowerCase() || '' : '';
    const documentExts = [
      'pdf',
      'doc',
      'docx',
      'xls',
      'xlsx',
      'ppt',
      'pptx',
      'txt',
      'csv',
      'zip',
      'rar',
    ];
    if (ext && documentExts.includes(ext)) return 'document';

    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';

    const documentMimes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/zip',
      'application/x-rar-compressed',
      'text/plain',
      'text/csv',
    ];
    if (documentMimes.includes(mimeType)) return 'document';
    return 'document';
  };

  const validateExtendedChatFileType = (file: File): { valid: boolean; error?: string } => {
    const baseResult = validateChatFileType(file);
    if (baseResult.valid) return baseResult;

    const mime = file.type || '';
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    const extendedMimes = new Set([
      'image/svg+xml',
      'video/x-msvideo',
      'video/webm',
      'audio/mpeg',
      'audio/wav',
      'audio/ogg',
      'audio/mp4',
      'audio/flac',
      'audio/aac',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/zip',
      'application/x-rar-compressed',
      'text/plain',
      'text/csv',
    ]);

    const extToMime: Record<string, string> = {
      svg: 'image/svg+xml',
      avi: 'video/x-msvideo',
      webm: 'video/webm',
      mp3: 'audio/mpeg',
      wav: 'audio/wav',
      ogg: 'audio/ogg',
      m4a: 'audio/mp4',
      flac: 'audio/flac',
      aac: 'audio/aac',
      xls: 'application/vnd.ms-excel',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ppt: 'application/vnd.ms-powerpoint',
      pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      txt: 'text/plain',
      csv: 'text/csv',
      zip: 'application/zip',
      rar: 'application/x-rar-compressed',
    };

    const resolved = mime || extToMime[ext] || '';
    if (extendedMimes.has(resolved)) {
      return { valid: true };
    }

    return {
      valid: false,
      error: `Invalid file format (${ext.toUpperCase() || 'unknown'}). Allowed: Images, Videos, Audio, PDF, DOC, DOCX, XLS, XLSX, PPT, TXT, CSV, ZIP.`,
    };
  };

  const handleFileSelect = (file: File) => {
    const valResult = validateExtendedChatFileType(file);
    if (!valResult.valid) {
      toast.error(valResult.error || 'Invalid file format.');
      return;
    }

    const mime = getMimeType(file);
    const isVideo = mime.startsWith('video/');
    const limitBytes = isVideo ? 50 * 1024 * 1024 : UPLOAD_MAX_FILE_SIZE_BYTES;
    const limitLabel = isVideo ? '50 MB' : '10 MB';

    if (file.size > limitBytes) {
      toast.error(`File too large (${formatFileSize(file.size)}). Maximum is ${limitLabel}.`);
      return;
    }
    setSelectedFile(file);

    // Generate preview for images, videos, and PDFs
    if (
      mime.startsWith('image/') ||
      mime.startsWith('video/') ||
      mime === 'application/pdf' ||
      file.name.toLowerCase().endsWith('.pdf')
    ) {
      const url = URL.createObjectURL(file);
      setFilePreviewUrl(url);
    } else {
      setFilePreviewUrl(null);
    }
  };

  const clearSelectedFile = () => {
    if (filePreviewUrl) {
      URL.revokeObjectURL(filePreviewUrl);
    }
    setSelectedFile(null);
    setFilePreviewUrl(null);
    setShowSelectedFileFullscreen(false);
    setShowCropModal(false);
  };

  const handleApplyCrop = async (cropAreaPixels: Area) => {
    if (!selectedFile || !filePreviewUrl) return;
    try {
      setIsApplyingCrop(true);
      const croppedFile = await createCroppedImageFile({
        imageSrc: filePreviewUrl,
        cropAreaPixels,
        fileName: selectedFile.name,
        outputType: selectedFile.type || 'image/jpeg',
      });
      if (filePreviewUrl) {
        URL.revokeObjectURL(filePreviewUrl);
      }
      const newPreviewUrl = URL.createObjectURL(croppedFile);
      setSelectedFile(croppedFile);
      setFilePreviewUrl(newPreviewUrl);
      setShowCropModal(false);
      toast.success('Image cropped successfully.');
    } catch (err) {
      console.error('Failed to crop image:', err);
      toast.error('Failed to crop image.');
    } finally {
      setIsApplyingCrop(false);
    }
  };

  const handleSend = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const cleanBody = emoji.emojify(inputMessage).trim();
    if (!cleanBody && !selectedFile) return;
    if (!conversationUuid) return;

    const fileToUpload = selectedFile;
    const fileMime = fileToUpload ? getMimeType(fileToUpload) : undefined;
    const fileResource =
      fileMime && fileToUpload ? getResourceType(fileMime, fileToUpload.name) : undefined;

    setInputMessage('');
    clearSelectedFile();
    setSuggestions([]);
    setSearchMatchRange(null);

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    enqueue(cleanBody, {
      file: fileToUpload || undefined,
      fileMimeType: fileMime,
      fileResourceType: fileResource,
    });

    userScrolledUpRef.current = false;
    scrollToBottom();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSuggestionIndex((prev) => (prev + 1) % suggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSuggestionIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        applyEmojiSuggestion(suggestions[suggestionIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setSuggestions([]);
        setSearchMatchRange(null);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!conversation) {
    return (
      <div className="flex h-full flex-col items-center justify-center bg-[#161618] p-8 text-center">
        <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-b from-[#2A2A2D] to-[#1C1C1E] border border-[#3A3A3C] shadow-lg">
          <svg
            className="w-8 h-8 text-zinc-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
        </div>
        <h3 className="mb-2 text-[20px] font-bold text-white tracking-tight">Your Messages</h3>
        <p className="max-w-[260px] text-[15px] text-zinc-400 leading-relaxed">
          Select a conversation from the sidebar or start a new one to begin chatting.
        </p>
      </div>
    );
  }

  const displayName =
    pickDisplayName(
      otherParticipant?.full_name,
      otherParticipant?.username,
      conversation.title,
      conversation.name,
    ) || 'Contact';

  // Prefer the fetched profile picture, fall back to conversation participant data
  const avatarUrl =
    otherProfile?.prof_pics ||
    otherProfile?.prof_pics_url ||
    otherProfile?.google_prof_pics ||
    otherParticipant?.profile_picture ||
    otherParticipant?.prof_pics ||
    otherParticipant?.google_prof_pics;

  const contextTitle = conversation.context?.title;
  const contextType = conversation.context?.type;
  const contextId = conversation.context?.id;

  // Resolve target URL and button label based on context type (Gig vs Service)
  const getContextInfo = () => {
    if (!contextId || !contextType) return null;
    const type = contextType.toLowerCase();
    if (type === 'gig' || type === 'gig_application') {
      return { url: `/gigs/${contextId}`, label: 'View Gig' };
    }
    if (type === 'service' || type === 'service_application') {
      return { url: `/services/${contextId}`, label: 'View Service' };
    }
    return null;
  };

  const contextInfo = getContextInfo();

  const scrollToMatch = (index: number) => {
    if (matchingMessages.length === 0) return;
    const targetMsg = matchingMessages[index];
    if (targetMsg?.id) {
      const el = document.getElementById(`msg-${targetMsg.id}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const handleNextMatch = () => {
    if (matchingMessages.length === 0) return;
    const nextIdx = (currentMatchIndex + 1) % matchingMessages.length;
    setCurrentMatchIndex(nextIdx);
    scrollToMatch(nextIdx);
  };

  const handlePrevMatch = () => {
    if (matchingMessages.length === 0) return;
    const prevIdx = (currentMatchIndex - 1 + matchingMessages.length) % matchingMessages.length;
    setCurrentMatchIndex(prevIdx);
    scrollToMatch(prevIdx);
  };

  return (
    <div className="flex h-full flex-col bg-[#161618] overflow-hidden min-h-0 relative">
      <div className="flex shrink-0 items-center justify-between px-4 py-3 bg-[#1C1C1E]/95 backdrop-blur-md border-b border-[#2C2C2E] z-10 sticky top-0">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {onBackMobile && (
            <button
              onClick={onBackMobile}
              className="mr-1 rounded-full p-2 -ml-2 text-[#1784FF] hover:bg-white/5 transition-colors lg:hidden shrink-0"
            >
              <FiArrowLeft size={22} />
            </button>
          )}

          {/* Sidebar Collapse Toggle Button (Desktop only, positioned to the left of the avatar) */}
          {onToggleSidebar && (
            <Tooltip content={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
              <button
                onClick={onToggleSidebar}
                className="hidden lg:flex h-9 w-9 items-center justify-center rounded-full text-zinc-400 hover:bg-white/10 hover:text-white transition-all shrink-0 cursor-pointer -ml-1.5 mr-0.5"
                aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {isSidebarCollapsed ? <FiChevronRight size={20} /> : <FiChevronLeft size={20} />}
              </button>
            </Tooltip>
          )}

          <div
            onClick={() => {
              if (otherParticipant?.username) {
                navigate({
                  to: '/profile/$identifier',
                  params: { identifier: otherParticipant.username },
                });
              }
            }}
            className="flex items-center gap-3 min-w-0 flex-1 cursor-pointer"
          >
            <ChatAvatar src={avatarUrl} name={displayName} size="md" />

            <div className="min-w-0 flex-1">
              {/* Gig/service leads the header so similarly-named conversations stay distinct */}
              {contextTitle ? (
                <h3 className="text-[16px] font-bold text-white truncate leading-tight flex items-center gap-1.5">
                  <FiBriefcase size={13} className="text-[#1784FF] shrink-0" />
                  <span className="truncate">{contextTitle}</span>
                </h3>
              ) : (
                <h3 className="text-[16px] font-bold text-white truncate leading-tight">
                  {displayName}
                </h3>
              )}
              {contextTitle && (
                <span className="text-[12px] text-zinc-400 font-medium truncate block mt-0.5">
                  with {displayName}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ── Header Action Buttons ────────────────────────────────────────── */}
        <div className="flex items-center gap-2 shrink-0">
          {contextInfo && (
            <a
              href={contextInfo.url}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1784FF]/10 text-[#1784FF] hover:bg-[#1784FF]/20 border border-[#1784FF]/20 text-[12px] font-bold transition-all"
              title={contextInfo.label}
            >
              <span>{contextInfo.label}</span>
              <FiExternalLink size={12} />
            </a>
          )}

          <Tooltip content="Search in conversation">
            <button
              onClick={() => {
                const nextState = !showSearchInput;
                setShowSearchInput(nextState);
                if (!nextState) {
                  setSearchQuery('');
                  setCurrentMatchIndex(0);
                }
              }}
              className={`h-9 w-9 flex items-center justify-center rounded-full transition-all cursor-pointer ${
                showSearchInput
                  ? 'bg-[#1784FF] text-white'
                  : 'text-zinc-400 hover:text-white hover:bg-white/10'
              }`}
              aria-label="Search in conversation"
            >
              <FiSearch size={18} />
            </button>
          </Tooltip>

          <Tooltip content="Shared media & attachments">
            <button
              onClick={() => setShowMediaPanel(!showMediaPanel)}
              className={`h-9 w-9 flex items-center justify-center rounded-full transition-all cursor-pointer relative ${
                showMediaPanel
                  ? 'bg-[#1784FF] text-white'
                  : 'text-zinc-400 hover:text-white hover:bg-white/10'
              }`}
              aria-label="Shared media & attachments"
            >
              <FiFolder size={18} />
              {sharedAttachments.length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#1784FF] px-1 text-[9px] font-bold text-white border border-[#1C1C1E]">
                  {sharedAttachments.length}
                </span>
              )}
            </button>
          </Tooltip>
        </div>
      </div>

      {/* ── In-Chat Keyword Search Banner ─────────────────────────────────── */}
      {showSearchInput && (
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-[#1C1C1E] border-b border-[#2C2C2E] animate-in slide-in-from-top-1 fade-in duration-200 z-10">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <FiSearch size={16} className="text-[#1784FF] shrink-0" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                const val = e.target.value;
                setSearchQuery(val);
                setCurrentMatchIndex(0);
                setTimeout(() => {
                  if (val.trim()) {
                    scrollToMatch(0);
                  }
                }, 50);
              }}
              placeholder="Search messages in this conversation..."
              className="w-full bg-transparent text-[14px] text-white placeholder:text-zinc-500 outline-none"
              autoFocus
            />
          </div>

          {searchQuery.trim() && (
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-[12px] font-semibold text-zinc-400 whitespace-nowrap">
                {matchingMessages.length > 0
                  ? `${currentMatchIndex + 1} of ${matchingMessages.length}`
                  : '0 matches'}
              </span>
              <div className="flex items-center gap-0.5 border-l border-[#3A3A3C] pl-2">
                <Tooltip content="Previous match">
                  <span className="inline-flex">
                    <button
                      type="button"
                      onClick={handlePrevMatch}
                      disabled={matchingMessages.length === 0}
                      className="p-1 rounded-md text-zinc-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                      aria-label="Previous match"
                    >
                      <FiChevronUp size={16} />
                    </button>
                  </span>
                </Tooltip>
                <Tooltip content="Next match">
                  <span className="inline-flex">
                    <button
                      type="button"
                      onClick={handleNextMatch}
                      disabled={matchingMessages.length === 0}
                      className="p-1 rounded-md text-zinc-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                      aria-label="Next match"
                    >
                      <FiChevronDown size={16} />
                    </button>
                  </span>
                </Tooltip>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={() => {
              setShowSearchInput(false);
              setSearchQuery('');
              setCurrentMatchIndex(0);
            }}
            className="text-zinc-400 hover:text-white text-[12px] font-semibold px-2.5 py-1 rounded-full bg-white/5 hover:bg-white/10 transition-colors shrink-0 cursor-pointer"
          >
            Done
          </button>
        </div>
      )}

      {/* ── Main Stream & Shared Media Drawer Container ─────────────────────── */}
      <div className="flex-1 flex overflow-hidden relative min-h-0">
        {/* ── Messages Stream ─────────────────────────────────────────────────── */}
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-3 sm:px-8 py-6 custom-modal-scrollbar min-h-0 bg-[#0F0F11]"
          style={{
            backgroundImage: 'radial-gradient(#2A2A2D 1px, transparent 1px)',
            backgroundSize: '24px 24px',
            overflowAnchor: 'none',
          }}
        >
          {isLoading ? (
            <div className="space-y-6">
              <div className="flex gap-3 animate-pulse">
                <div className="w-8 h-8 rounded-full bg-white/5 shrink-0 mt-auto" />
                <div className="h-12 w-48 rounded-2xl rounded-bl-sm bg-white/10" />
              </div>
              <div className="flex gap-3 animate-pulse justify-end">
                <div className="h-16 w-64 rounded-2xl rounded-br-sm bg-[#1784FF]/20" />
              </div>
              <div className="flex gap-3 animate-pulse">
                <div className="w-8 h-8 rounded-full bg-white/5 shrink-0 mt-auto" />
                <div className="h-20 w-72 rounded-2xl rounded-bl-sm bg-white/10" />
              </div>
            </div>
          ) : sortedMessages.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center pb-20">
              <div className="bg-[#1C1C1E] rounded-2xl p-4 border border-[#2C2C2E] max-w-[280px]">
                <p className="text-[14px] font-medium text-white mb-1">No messages yet</p>
                <p className="text-[13px] text-zinc-400">
                  Send a greeting to start the conversation.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col">
              {isFetchingNextPage && (
                <div className="flex justify-center py-2 shrink-0">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#1784FF]"></div>
                </div>
              )}
              {sortedMessages.map((msg, index, arr) => {
                const prevMsg = index > 0 ? arr[index - 1] : null;
                const nextMsg = index < arr.length - 1 ? arr[index + 1] : null;

                const isCurrentUser = msg.sender_id === currentUserId;
                const prevIsSameUser = prevMsg?.sender_id === msg.sender_id;
                const nextIsSameUser = nextMsg?.sender_id === msg.sender_id;

                const msgTime = new Date(msg.created_at || '').getTime();
                const prevTime = prevMsg ? new Date(prevMsg.created_at || '').getTime() : 0;
                const nextTime = nextMsg ? new Date(nextMsg.created_at || '').getTime() : 0;

                const isClusteredWithPrev = prevIsSameUser && msgTime - prevTime < 60000;
                const isClusteredWithNext = nextIsSameUser && nextTime - msgTime < 60000;

                const msgDateLabel = formatDateLabel(msg.created_at);
                const prevDateLabel = prevMsg ? formatDateLabel(prevMsg.created_at) : null;
                const showDateLabel = msgDateLabel !== prevDateLabel;

                const isSearchFocused =
                  matchingMessages.length > 0 &&
                  matchingMessages[currentMatchIndex]?.id === msg.id &&
                  Boolean(searchQuery.trim());

                return (
                  <React.Fragment key={(msg as any).localId || msg.id || index}>
                    {showDateLabel && (
                      <div className="flex items-center my-6">
                        <div className="flex-1 h-px bg-[#2C2C2E]/60"></div>
                        <span
                          className={`mx-4 px-3 py-1 rounded-full text-[12px] font-medium shadow-sm border ${
                            msgDateLabel === 'Today'
                              ? 'bg-[#1784FF]/10 border-[#1784FF]/30 text-[#1784FF]'
                              : 'bg-[#1C1C1E]/80 border-[#2C2C2E] text-zinc-300'
                          }`}
                        >
                          {msgDateLabel}
                        </span>
                        <div className="flex-1 h-px bg-[#2C2C2E]/60"></div>
                      </div>
                    )}
                    <div className={`mb-${isClusteredWithNext ? '1' : '4'}`}>
                      <ChatMessageBubble
                        message={msg}
                        isCurrentUser={isCurrentUser}
                        isClusteredWithPrev={isClusteredWithPrev}
                        isClusteredWithNext={isClusteredWithNext}
                        senderAvatar={!isCurrentUser ? avatarUrl : null}
                        isLastMessage={index === arr.length - 1}
                        isRead={Boolean(msg.is_read || msg.read || msg.read_at)}
                        searchQuery={searchQuery}
                        isSearchFocused={isSearchFocused}
                        queueStatus={(msg as any).queueStatus}
                        uploadProgress={(msg as any).uploadProgress}
                        onRetry={() => (msg as any).localId && retryMessage((msg as any).localId)}
                        onAvatarClick={() => {
                          if (otherParticipant?.username) {
                            navigate({
                              to: '/profile/$identifier',
                              params: { identifier: otherParticipant.username },
                            });
                          }
                        }}
                      />
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          )}
          <div ref={messagesEndRef} className="h-4" />
        </div>

        {/* ── Shared Media Panel Drawer ────────────────────────────────────────── */}
        {showMediaPanel && (
          <div className="w-80 border-l border-[#2C2C2E] bg-[#161618] flex flex-col h-full z-20 animate-in slide-in-from-right duration-200 shrink-0">
            <div className="flex items-center justify-between p-4 border-b border-[#2C2C2E]">
              <div className="flex items-center gap-2">
                <FiFolder size={18} className="text-[#1784FF]" />
                <h4 className="text-white text-[15px] font-bold">Shared Media</h4>
              </div>
              <button
                onClick={() => setShowMediaPanel(false)}
                className="text-zinc-400 hover:text-white p-1.5 rounded-full hover:bg-white/10 transition-colors cursor-pointer"
              >
                <FiX size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-modal-scrollbar">
              {sharedAttachments.length === 0 ? (
                <div className="text-center py-12 text-zinc-500 text-xs">
                  No shared files or media in this conversation yet.
                </div>
              ) : (
                sharedAttachments.map((att: ChatAttachment, idx: number) => {
                  const url = att.url || att.file_url || '';
                  const { fileName, ext, typeLabel } = getAttachmentDisplayInfo(att);
                  return (
                    <div
                      key={idx}
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreviewAttachment(att);
                      }}
                      className="flex items-center gap-3 p-3 rounded-2xl bg-[#1C1C1E] border border-[#2C2C2E] hover:border-[#1784FF]/50 transition-all group cursor-pointer"
                    >
                      {['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext) && url ? (
                        <div className="w-10 h-10 rounded-xl overflow-hidden shrink-0 border border-[#2C2C2E] bg-[#2A2A2D]">
                          <img src={url} className="w-full h-full object-cover" alt={fileName} />
                        </div>
                      ) : ['mp4', 'mov', 'avi', 'webm', 'mkv'].includes(ext) && url ? (
                        <div className="w-10 h-10 rounded-xl overflow-hidden shrink-0 border border-[#2C2C2E] bg-black flex items-center justify-center relative">
                          <video
                            src={url}
                            className="w-full h-full object-cover"
                            muted
                            playsInline
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-white">
                            <FiVideo size={14} />
                          </div>
                        </div>
                      ) : getDocumentThumbnailUrl(url) ? (
                        <div className="w-10 h-10 rounded-xl overflow-hidden shrink-0 border border-[#2C2C2E] bg-white">
                          <img
                            src={getDocumentThumbnailUrl(url)!}
                            className="w-full h-full object-cover"
                            alt={fileName}
                          />
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded-xl bg-[#2A2A2D] text-[#1784FF] group-hover:bg-[#1784FF] group-hover:text-white transition-colors shrink-0 flex items-center justify-center">
                          {ext === 'pdf' ? (
                            <FiFileText size={16} className="text-red-400 group-hover:text-white" />
                          ) : ['mp3', 'wav', 'ogg', 'm4a', 'flac', 'aac'].includes(ext) ? (
                            <FiMusic size={16} />
                          ) : (
                            <FiFileText size={16} />
                          )}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-[13px] font-medium truncate" title={fileName}>
                          {fileName}
                        </p>
                        <p className="text-zinc-500 text-[11px] mt-0.5">
                          {typeLabel}
                          {att.file_size ? ` • ${formatFileSize(att.file_size)}` : ''}
                        </p>
                      </div>
                      <Tooltip content="Download file">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            triggerFileDownload(url, fileName);
                          }}
                          className="p-1.5 rounded-full hover:bg-white/10 text-zinc-400 group-hover:text-white transition-colors shrink-0 cursor-pointer"
                          aria-label="Download file"
                        >
                          <FiDownload size={14} />
                        </button>
                      </Tooltip>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Floating Scroll to Bottom Button */}
      {showScrollButton && (
        <Tooltip content="Scroll to bottom">
          <button
            type="button"
            onClick={() => scrollToBottom()}
            className={`absolute right-6 p-2.5 bg-[#1C1C1E] border border-[#2C2C2E] rounded-full text-zinc-300 hover:text-white shadow-xl hover:bg-[#2A2A2D] transition-all z-20 cursor-pointer ${
              selectedFile ? 'bottom-44' : 'bottom-24'
            }`}
            aria-label="Scroll to bottom"
          >
            <FiChevronDown size={20} />
          </button>
        </Tooltip>
      )}

      {/* ── Selected File Preview ───────────────────────────────────────── */}
      {selectedFile && (
        <div className="px-4 py-3 bg-[#1C1C1E] border-t border-[#2C2C2E]">
          <div className="flex items-start gap-3">
            {/* Thumbnail preview for images/videos */}
            {filePreviewUrl && getMimeType(selectedFile).startsWith('image/') && (
              <div className="relative w-16 h-16 rounded-xl overflow-hidden border border-[#3A3A3C] shrink-0 group/preview">
                <img src={filePreviewUrl} alt="Preview" className="w-full h-full object-cover" />
                <Tooltip content="Enlarge Preview">
                  <button
                    type="button"
                    onClick={() => setShowSelectedFileFullscreen(true)}
                    className="absolute inset-0 bg-black/40 hover:bg-black/60 flex items-center justify-center transition-all opacity-0 group-hover/preview:opacity-100 cursor-pointer text-white"
                    aria-label="Enlarge Preview"
                  >
                    <FiMaximize2 size={16} />
                  </button>
                </Tooltip>
              </div>
            )}
            {filePreviewUrl && getMimeType(selectedFile).startsWith('video/') && (
              <div className="relative w-16 h-16 rounded-xl overflow-hidden border border-[#3A3A3C] shrink-0 bg-black flex items-center justify-center group/preview">
                <video src={filePreviewUrl} className="w-full h-full object-cover" muted />
                <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                  <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
                <Tooltip content="Enlarge Preview">
                  <button
                    type="button"
                    onClick={() => setShowSelectedFileFullscreen(true)}
                    className="absolute inset-0 bg-black/40 hover:bg-black/60 flex items-center justify-center transition-all opacity-0 group-hover/preview:opacity-100 cursor-pointer text-white"
                    aria-label="Enlarge Preview"
                  >
                    <FiMaximize2 size={16} />
                  </button>
                </Tooltip>
              </div>
            )}

            {filePreviewUrl &&
              (getMimeType(selectedFile) === 'application/pdf' ||
                selectedFile.name.toLowerCase().endsWith('.pdf')) && (
                <div
                  className="relative w-20 h-20 rounded-xl overflow-hidden border border-[#3A3A3C] bg-white shrink-0 group/preview cursor-pointer"
                  onClick={() => setShowSelectedFileFullscreen(true)}
                >
                  <iframe
                    src={`${filePreviewUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                    title="PDF Preview"
                    className="w-full h-full border-0 pointer-events-none select-none opacity-90 group-hover/preview:opacity-100"
                  />
                  <Tooltip content="Expand PDF">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSelectedFileFullscreen(true);
                      }}
                      className="absolute inset-0 bg-black/40 hover:bg-black/60 flex items-center justify-center transition-all opacity-0 group-hover/preview:opacity-100 cursor-pointer text-white"
                      aria-label="Expand PDF"
                    >
                      <FiMaximize2 size={16} />
                    </button>
                  </Tooltip>
                </div>
              )}

            <div className="flex-1 min-w-0 flex items-center justify-between gap-2.5 bg-[#2A2A2D] px-3 py-2 rounded-lg border border-[#3A3A3C]">
              <div className="flex items-center gap-2.5 min-w-0 flex-1">
                <FiPaperclip size={14} className="text-[#1784FF] shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="truncate font-medium text-[13px] text-zinc-200 block">
                    {selectedFile.name}
                  </span>
                  <span className="text-zinc-500 text-[11px] block">
                    {formatFileSize(selectedFile.size)}
                  </span>
                </div>
              </div>

              {getMimeType(selectedFile).startsWith('image/') && (
                <button
                  type="button"
                  onClick={() => setShowCropModal(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#1784FF]/15 text-[#1784FF] border border-[#1784FF]/30 hover:bg-[#1784FF]/25 text-xs font-semibold transition-all shrink-0 cursor-pointer"
                  title="Crop image before sending"
                >
                  <FiCrop size={14} />
                  <span>Crop</span>
                </button>
              )}
            </div>

            <button
              onClick={clearSelectedFile}
              className="p-1.5 rounded-full hover:bg-white/10 hover:text-red-400 text-zinc-400 transition shrink-0 mt-1 cursor-pointer"
            >
              <FiX size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Fullscreen pre-send media preview */}
      {showSelectedFileFullscreen && selectedFile && (
        <div
          className="fixed inset-0 z-[300] bg-black/95 backdrop-blur-sm flex flex-col justify-between"
          onClick={() => setShowSelectedFileFullscreen(false)}
        >
          {/* Header Info & Controls */}
          <div
            className="flex items-center justify-between p-4 bg-gradient-to-b from-black/80 to-transparent z-10 w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="min-w-0 flex-1 pr-4">
              <h4 className="text-white text-[15px] font-semibold truncate">{selectedFile.name}</h4>
              <p className="text-zinc-400 text-[12px] mt-0.5">
                {formatFileSize(selectedFile.size)}
              </p>
            </div>
            <Tooltip content="Close">
              <button
                onClick={() => setShowSelectedFileFullscreen(false)}
                className="p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
                aria-label="Close"
              >
                <FiX size={20} />
              </button>
            </Tooltip>
          </div>

          {/* Media preview area */}
          <div className="flex-1 flex items-center justify-center p-4 min-h-0 relative select-none">
            <div
              className="max-w-full max-h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              {getMimeType(selectedFile).startsWith('image/') && filePreviewUrl && (
                <img
                  src={filePreviewUrl}
                  alt="Selected Preview"
                  className="max-w-full max-h-[80vh] md:max-h-[85vh] object-contain rounded-lg shadow-2xl"
                />
              )}
              {getMimeType(selectedFile).startsWith('video/') && filePreviewUrl && (
                <video
                  src={filePreviewUrl}
                  controls
                  autoPlay
                  className="max-w-full max-h-[80vh] md:max-h-[85vh] rounded-lg shadow-2xl"
                />
              )}
              {(getMimeType(selectedFile) === 'application/pdf' ||
                selectedFile.name.toLowerCase().endsWith('.pdf')) &&
                filePreviewUrl && (
                  <div className="w-[92vw] max-w-5xl h-[82vh] bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] shadow-2xl overflow-hidden flex flex-col">
                    <div className="flex items-center justify-between px-4 py-2.5 bg-[#2A2A2D] border-b border-[#3A3A3C]">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded shrink-0">
                          PDF
                        </span>
                        <span className="text-white text-xs font-semibold truncate">
                          {selectedFile.name}
                        </span>
                      </div>
                    </div>
                    <iframe
                      src={`${filePreviewUrl}#toolbar=1&navpanes=0`}
                      title={selectedFile.name}
                      className="w-full flex-1 border-0 bg-white"
                    />
                  </div>
                )}
            </div>
          </div>

          {/* Footer space */}
          <div className="h-16 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
        </div>
      )}

      {/* ── Input Bar ───────────────────────────────────────────────────────── */}
      <div className="p-3 sm:p-4 bg-[#1C1C1E] border-t border-[#2C2C2E] shrink-0 relative">
        {/* Emoji Command Suggestions Autocomplete Dropdown */}
        {suggestions.length > 0 && (
          <div className="absolute bottom-full left-4 sm:left-16 mb-3 z-50 w-64 bg-[#1C1C1E] border border-[#3A3A3C] rounded-2xl shadow-2xl overflow-hidden py-1">
            <div className="px-3 py-1.5 text-[11px] font-bold text-zinc-400 uppercase tracking-wider border-b border-[#2C2C2E] flex items-center justify-between">
              <span>Emoji Commands</span>
              <span className="text-[10px] text-zinc-500 font-normal">↑↓ to navigate</span>
            </div>
            <div className="max-h-48 overflow-y-auto custom-scrollbar">
              {suggestions.map((item, idx) => (
                <button
                  key={`${item.name}-${idx}`}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    applyEmojiSuggestion(item);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm transition-colors cursor-pointer ${
                    idx === suggestionIndex
                      ? 'bg-[#1784FF] text-white font-medium'
                      : 'text-zinc-200 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center gap-2.5 truncate">
                    <span className="text-lg leading-none">{item.emoji}</span>
                    <span className="truncate text-[13px]">{`:${item.name}:`}</span>
                  </div>
                  <span
                    className={`text-[10px] ${idx === suggestionIndex ? 'text-white/80' : 'text-zinc-500'}`}
                  >
                    Enter
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {showEmojiPicker && (
          <div
            ref={emojiPickerRef}
            className="absolute bottom-full left-4 sm:left-6 mb-3 z-50 shadow-2xl rounded-2xl overflow-hidden border border-[#3A3A3C]"
          >
            <EmojiPicker
              onEmojiClick={handleEmojiClick}
              theme={Theme.DARK}
              lazyLoadEmojis={true}
              searchPlaceHolder="Search emoji..."
              width={320}
              height={400}
            />
          </div>
        )}
        <form onSubmit={handleSend} className="flex items-end gap-2">
          {/* Action Buttons */}
          <div className="flex items-center gap-1 mb-1.5">
            <input
              type="file"
              ref={fileInputRef}
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  handleFileSelect(e.target.files[0]);
                }
                e.target.value = '';
              }}
              className="hidden"
              accept={CHAT_FILE_ACCEPT_ATTRIBUTE}
            />
            <Tooltip content="Attach file">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-zinc-400 hover:bg-white/10 hover:text-[#1784FF] transition-colors cursor-pointer"
                aria-label="Attach file"
              >
                <FiPlus size={22} />
              </button>
            </Tooltip>
            <Tooltip content="Emoji">
              <button
                type="button"
                onClick={() => setShowEmojiPicker((prev) => !prev)}
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors cursor-pointer ${
                  showEmojiPicker
                    ? 'bg-white/15 text-amber-400'
                    : 'text-zinc-400 hover:bg-white/10 hover:text-amber-400'
                }`}
                aria-label="Emoji"
              >
                <FiSmile size={20} />
              </button>
            </Tooltip>
          </div>

          {/* Text Input */}
          <div className="flex-1 relative bg-[#2A2A2D] rounded-2xl border border-[#3A3A3C] shadow-inner focus-within:border-[#1784FF]/50 focus-within:ring-1 focus-within:ring-[#1784FF]/30 transition-all overflow-hidden flex items-end">
            <textarea
              ref={textareaRef}
              value={inputMessage}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              placeholder="Message..."
              className="w-full bg-transparent px-4 py-3 text-[15px] text-white placeholder:text-zinc-500 outline-none resize-none max-h-[120px] custom-scrollbar min-h-[44px]"
              rows={1}
            />
          </div>

          {/* Send Button */}
          <div className="mb-1">
            {inputMessage.trim() || selectedFile ? (
              <button
                type="submit"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-[#1784FF] hover:bg-blue-500 text-white shadow-md transition-all cursor-pointer hover:scale-105 active:scale-95"
              >
                <FiSend size={18} className="translate-x-[1px] translate-y-[1px]" />
              </button>
            ) : (
              <button
                type="button"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-[#2A2A2D] text-zinc-500 cursor-default"
                disabled
              >
                <FiSend size={18} className="translate-x-[1px] translate-y-[1px]" />
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Freeform Image Crop Modal */}
      <FreeformImageCropModal
        isOpen={showCropModal}
        imageSrc={filePreviewUrl}
        isSubmitting={isApplyingCrop}
        onCancel={() => setShowCropModal(false)}
        onConfirm={handleApplyCrop}
      />

      {/* Fullscreen Preview for Shared Media */}
      {previewAttachment && (
        <FullscreenMediaViewer
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(null)}
        />
      )}
    </div>
  );
};
